/**
 * Industry Map - Manager/Owner Only
 * 
 * This is the advanced map view with OCC competitor data, fleet tracking,
 * market intelligence, and weather overlays. Available to managers and
 * oil owners only via Settings > Industry Map.
 * 
 * For pumpers, use SatelliteCommand.tsx which is the main map with well popups,
 * tank monitoring, and camera views.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow, MarkerClusterer, OverlayView } from '@react-google-maps/api';
import { AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { getGoogleMapsLoaderOptions, DEFAULT_MAP_CENTER, NIGHT_MODE_STYLES } from '../../utils/maps/googleMapsConfig';

// Services & Components
import { safetyService } from '../../utils/services/safetyService';
import { occWellService, OCCWell } from '../../utils/intelligence/occWellService';
import SmartWellCard from '../map/SmartWellCard';
import WeatherOverlay from '../map/WeatherOverlay';
import MapControls, { MapLayerConfig } from '../map/MapControls';
import { X, Search, Radio } from 'lucide-react';

// --- Interfaces ---

interface IndustryMapProps {
  wells: any[];
  employees?: any[];
  tickets?: any[];
  tasks?: any[];
  onClose: () => void;
  onSelectWell?: (well: any) => void;
  onAddEntry?: (well: any) => void;
  onCreateTicket?: (well: any) => void;
  onCreateTask?: (well: any) => void;
  onOpenTanks?: (well: any) => void;
  onOpenCams?: (well: any) => void;
  onNavigate?: (well: any) => void;
}

interface FleetMember {
  id: string;
  name: string;
  lat: number;
  lng: number;
  heading: number;
  status: 'moving' | 'stopped' | 'idle' | 'alert';
  speed?: number;
  driver?: string;
  updatedAt?: Date;
}

const mapContainerStyle = { width: '100%', height: '100%' };

// Custom Pumpjack SVG Icon
// Detailed Pumpjack SVG Icon
const PUMPJACK_ICON = "M4,18 L20,18 L22,22 L2,22 Z M14,10 L14,18 M10,18 L10,14 L4,8 L18,8 M18,8 L20,6 M10,14 L8,18 M16,12 C16,13.1 15.1,14 14,14 C12.9,14 12,13.1 12,12 C12,10.9 12.9,10 14,10 C15.1,10 16,10.9 16,12 Z";

// Enterprise Map Options
const ENTERPRISE_MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  tilt: 45,
  heading: 0,
};

export default function IndustryMap({
  wells,
  onClose,
  onSelectWell,
  onAddEntry,
  onCreateTicket,
  onCreateTask,
  onOpenTanks,
  onOpenCams,
  onNavigate,
}: IndustryMapProps) {
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsLoaderOptions());

  // -- State --
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null); // Keep ref for imperative methods if needed, or sync them
  
  // Sync map state to ref for existing functions that use ref
  useEffect(() => {
    mapRef.current = map;
  }, [map]);

  const [selectedEntity, setSelectedEntity] = useState<{ type: string; data: any } | null>(null);
  const [fleet, setFleet] = useState<FleetMember[]>([]);
  const [competitors, setCompetitors] = useState<OCCWell[]>([]);

  // Default Layer Config
  const [config, setConfig] = useState<MapLayerConfig>({
    wells: true,
    fleet: true,
    competitors: true,
    weather: false,
    heatmap: false,
    satellite: true,
  });

  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER);
  const [mapStyles, setMapStyles] = useState<any[]>(NIGHT_MODE_STYLES);

  // User location state with heading (for direction arrow)
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [userHeading, setUserHeading] = useState<number | null>(null);

  // Watch user location
  useEffect(() => {
    if (!navigator.geolocation) return;
    
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        if (position.coords.heading !== null && !isNaN(position.coords.heading)) {
          setUserHeading(position.coords.heading);
        }
      },
      (error) => console.warn('Location error:', error.message),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );

    // Device orientation for heading fallback
    const handleOrientation = (event: DeviceOrientationEvent) => {
      if (event.alpha !== null && userHeading === null) {
        setUserHeading(360 - event.alpha);
      }
    };

    if ('DeviceOrientationEvent' in window) {
      window.addEventListener('deviceorientation', handleOrientation);
    }

    return () => {
      navigator.geolocation.clearWatch(watchId);
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, []);

  // -- Day/Night Auto-Theme Logic --
  useEffect(() => {
    const hour = new Date().getHours();
    const isDay = hour > 6 && hour < 18;

    if (!config.satellite) {
      if (isDay) setMapStyles([]); // Standard Light Mode
      else setMapStyles(NIGHT_MODE_STYLES); // Enterprise Dark Mode
    }
  }, [config.satellite]);

  // -- Fleet Tracking Service --
  useEffect(() => {
    const loadFleet = async () => {
      try {
        const data = await safetyService.getActiveFleet();
        setFleet(data as FleetMember[]);
      } catch (error) {
        console.error("Failed to load fleet", error);
      }
    };
    loadFleet();
    const interval = setInterval(loadFleet, 30000);
    return () => clearInterval(interval);
  }, []);

  // -- Competitor Data (Optimized) --
  const loadCompetitors = useCallback(async () => {
    if (!mapRef.current || !config.competitors) return;
    const bounds = mapRef.current.getBounds();
    if (bounds) {
      try {
        const data = await occWellService.fetchNearbyWells(bounds);
        setCompetitors(prev => {
          const existing = new Set(prev.map(p => p.id));
          const newWells = data.filter(d => !existing.has(d.id));

          if (newWells.length === 0) return prev; // PREVENT RE-RENDER if no new data

          return [...prev, ...newWells];
        });
      } catch (e) { console.error(e); }
    }
  }, [config.competitors]);

  // -- Keyboard Shortcuts --
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      switch (e.key.toLowerCase()) {
        case 'w': setConfig(c => ({ ...c, wells: !c.wells })); break;
        case 'f': setConfig(c => ({ ...c, fleet: !c.fleet })); break;
        case 'c':
          setConfig(c => {
            const newVal = !c.competitors;
            if (newVal) toast.info('Loading market intelligence...');
            return { ...c, competitors: newVal };
          });
          break;
        case 's': setConfig(c => ({ ...c, satellite: !c.satellite })); break;
        case 'r': setConfig(c => ({ ...c, weather: !c.weather })); break;
        case 'h': setConfig(c => ({ ...c, heatmap: !c.heatmap })); break;
        case 'escape':
          if (selectedEntity) setSelectedEntity(null);
          else onClose();
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntity, onClose]);

  // -- Map Handlers --
  const onMapLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
  }, []);

  // Initial Center Logic
  const initialCenterRef = useRef(false);
  useEffect(() => {
    if (map && !initialCenterRef.current) {
      const wellsWithCoords = wells.filter(w => 
        (w.latitude && w.longitude) || w.coordinates
      );
      
      if (wellsWithCoords.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        wellsWithCoords.forEach(w => {
          if (w.latitude && w.longitude) bounds.extend({ lat: w.latitude, lng: w.longitude });
          else if (w.coordinates) bounds.extend(w.coordinates);
        });
        
        // Include user location in bounds if nearby
        if (userLocation) {
          const userNearWells = wellsWithCoords.some(w => {
            const wLat = w.latitude || w.coordinates?.lat;
            const wLng = w.longitude || w.coordinates?.lng;
            if (!wLat || !wLng) return false;
            const dist = Math.sqrt(
              Math.pow(wLat - userLocation.lat, 2) +
              Math.pow(wLng - userLocation.lng, 2)
            );
            return dist < 0.5; // ~50km
          });
          if (userNearWells) {
            bounds.extend(userLocation);
          }
        }
        
        map.fitBounds(bounds, { top: 100, bottom: 100, left: 100, right: 100 });
        initialCenterRef.current = true;
      } else if (userLocation) {
        // Fallback to user location
        map.setCenter(userLocation);
        map.setZoom(14);
        initialCenterRef.current = true;
      }
    }
  }, [map, wells, userLocation]);

  const onMapIdle = useCallback(() => {
    // Basic debounce logic could be here, but the state check above handles the critical perf issue causes by re-renders.
    // We will rely on onIdle only firing when movement *stops*, which is natural debounce.
    if (config.competitors) {
      // Small timeout to allow map to settle completely
      setTimeout(loadCompetitors, 200);
    }

    // Update center state for weather overlay
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      if (center) setMapCenter({ lat: center.lat(), lng: center.lng() });
    }
  }, [config.competitors, loadCompetitors]);

  // -- Cinematic Fly-To --
  const flyToLocation = useCallback((lat: number, lng: number) => {
    if (!mapRef.current) return;
    mapRef.current.panTo({ lat, lng });
    setTimeout(() => {
      mapRef.current?.setZoom(16);
      mapRef.current?.setTilt(45);
      mapRef.current?.setHeading(0);
    }, 500);
  }, []);

  // -- Heatmap Logic --
  const getMarkerColor = (well: any) => {
    if (config.heatmap) {
      const prod = (well.oilProduction || 0) + (well.gasProduction || 0) / 6;
      if (prod > 100) return '#22c55e'; // High
      if (prod > 50) return '#eab308';  // Med
      if (prod > 0) return '#f97316';   // Low
      return '#ef4444';                 // 0
    }

    const isActive = ['active', 'producing', 'flowing'].includes(well.status?.toLowerCase());
    const isCritical = well.status === 'critical' || well.hasAlert;

    if (isCritical) return '#ef4444';
    if (isActive) return '#10b981';
    return '#f59e0b';
  };

  if (loadError) return <div className="p-8 text-center text-red-500">Map Error: {loadError.message}</div>;
  if (!isLoaded) return <div className="fixed inset-0 bg-slate-950 flex items-center justify-center text-blue-500"><Radio className="animate-spin h-12 w-12" /></div>;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950">

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 h-16 z-[9999] flex items-center justify-between px-4">
        <div className="bg-slate-950/80 backdrop-blur-md rounded-full px-4 py-2 border border-white/10 flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-300">Industry Map <span className="text-slate-500 mx-2">|</span> {wells.length} Assets</span>
        </div>
        <button 
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }} 
          className="bg-red-600 hover:bg-red-500 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-xl border-2 border-red-400 transform transition-transform hover:scale-110 cursor-pointer"
          style={{ zIndex: 99999 }}
        >
          <X className="h-6 w-6 text-white" />
        </button>
      </div>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={DEFAULT_MAP_CENTER}
        zoom={6}
        options={{
          ...ENTERPRISE_MAP_OPTIONS,
          mapTypeId: config.satellite ? 'hybrid' : 'roadmap',
          styles: config.satellite ? [] : mapStyles
        }}
        onLoad={onMapLoad}
        onIdle={onMapIdle}
        onClick={() => setSelectedEntity(null)}
      >
        {/* === WELLS LAYER === */}
        {config.wells && wells.map(well => {
          const lat = well.latitude || well.coordinates?.lat;
          const lng = well.longitude || well.coordinates?.lng;
          if (!lat || !lng) return null;

          const color = getMarkerColor(well);

          return (
            <Marker
              key={well.id}
              position={{ lat, lng }}
              onClick={() => {
                flyToLocation(lat, lng);
                setSelectedEntity({ type: 'well', data: well });
                if (onSelectWell) onSelectWell(well);
              }}
              icon={{
                path: PUMPJACK_ICON,
                scale: config.heatmap ? 1.5 : 1.2,
                fillColor: color,
                fillOpacity: 1,
                strokeWeight: 1,
                strokeColor: '#ffffff',
                anchor: new google.maps.Point(12, 12),
              }}
              label={config.heatmap ? undefined : {
                text: well.name?.substring(0, 3).toUpperCase(),
                color: '#ffffff',
                fontSize: '9px',
                fontWeight: 'bold',
                className: 'mt-5'
              }}
            />
          );
        })}

        {/* === FLEET LAYER === */}
        {config.fleet && fleet.map(member => (
          <Marker
            key={member.id}
            position={{ lat: member.lat, lng: member.lng }}
            onClick={() => {
              flyToLocation(member.lat, member.lng);
              setSelectedEntity({ type: 'fleet', data: member });
            }}
            icon={{
              path: "M10,0 C4.5,0 0,4.5 0,10 C0,18 10,24 10,24 C10,24 20,18 20,10 C20,4.5 15.5,0 10,0 Z",
              scale: 1.5,
              fillColor: member.status === 'moving' ? '#10b981' : '#f59e0b',
              fillOpacity: 1,
              strokeWeight: 2,
              strokeColor: '#fff',
            }}
            zIndex={100}
          />
        ))}

        {/* === COMPETITORS LAYER === */}
        {config.competitors && (
          <MarkerClusterer>
            {(clusterer) => (
              <>
                {competitors.map(comp => (
                  <Marker
                    key={comp.id}
                    position={{ lat: comp.lat, lng: comp.lng }}
                    clusterer={clusterer}
                    icon={{
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: 5,
                      fillColor: '#64748b',
                      fillOpacity: 0.7,
                      strokeWeight: 1,
                      strokeColor: '#334155'
                    }}
                    onClick={() => setSelectedEntity({ type: 'competitor', data: comp })}
                  />
                ))}
              </>
            )}
          </MarkerClusterer>
        )}

        {/* 📍 User Location Marker with Direction Arrow */}
        {userLocation && (
          <>
            {/* Accuracy pulse circle */}
            <Marker
              position={userLocation}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: '#3B82F6',
                fillOpacity: 0.15,
                strokeColor: '#3B82F6',
                strokeWeight: 1,
                scale: 25,
              }}
              zIndex={998}
            />
            
            {/* Direction arrow */}
            {userHeading !== null && (
              <Marker
                position={userLocation}
                icon={{
                  path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  fillColor: '#3B82F6',
                  fillOpacity: 1,
                  strokeColor: '#FFFFFF',
                  strokeWeight: 2,
                  scale: 5,
                  rotation: userHeading,
                  anchor: new google.maps.Point(0, 2.5),
                }}
                zIndex={1001}
              />
            )}
            
            {/* Center blue dot */}
            <Marker
              position={userLocation}
              icon={{
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: '#3B82F6',
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: 3,
                scale: 8,
              }}
              zIndex={1000}
            />
          </>
        )}

        {/* === POPUPS === */}
        <AnimatePresence>
          {selectedEntity?.type === 'well' && (
            <OverlayView
              position={{
                lat: selectedEntity.data.latitude || selectedEntity.data.coordinates?.lat,
                lng: selectedEntity.data.longitude || selectedEntity.data.coordinates?.lng
              }}
              mapPaneName={OverlayView.FLOAT_PANE}
            >
              <div className="relative">
                <SmartWellCard
                  well={selectedEntity.data}
                  onClose={() => setSelectedEntity(null)}
                  onNavigate={() => {
                    if (onNavigate) {
                      onNavigate(selectedEntity.data);
                    } else {
                      // Fallback to Google Maps if no handler provided
                      const lat = selectedEntity.data.latitude || selectedEntity.data.coordinates?.lat;
                      const lng = selectedEntity.data.longitude || selectedEntity.data.coordinates?.lng;
                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                    }
                  }}
                  onCall={() => toast.info('Calling pumper assigned to this well...')}
                  onAddEntry={() => onAddEntry?.(selectedEntity.data)}
                  onCreateTicket={() => onCreateTicket?.(selectedEntity.data)}
                  onCreateTask={() => onCreateTask?.(selectedEntity.data)}
                  onOpenTanks={() => onOpenTanks?.(selectedEntity.data)}
                  onOpenCams={() => onOpenCams?.(selectedEntity.data)}
                />
              </div>
            </OverlayView>
          )}
        </AnimatePresence>

        {selectedEntity?.type === 'fleet' && (
          <InfoWindow
            position={{ lat: selectedEntity.data.lat, lng: selectedEntity.data.lng }}
            onCloseClick={() => setSelectedEntity(null)}
          >
            <div className="p-2 text-slate-900">
              <h3 className="font-bold">{selectedEntity.data.name}</h3>
              <div className="text-xs">Status: {selectedEntity.data.status}</div>
              <div className="text-xs">Speed: {selectedEntity.data.speed} mph</div>
            </div>
          </InfoWindow>
        )}

        {selectedEntity?.type === 'competitor' && (
          <InfoWindow
            position={{ lat: selectedEntity.data.lat, lng: selectedEntity.data.lng }}
            onCloseClick={() => setSelectedEntity(null)}
          >
            <div className="p-2 text-slate-900">
              <h3 className="font-bold">{selectedEntity.data.operator}</h3>
              <div className="text-xs">{selectedEntity.data.name}</div>
            </div>
          </InfoWindow>
        )}

      </GoogleMap>

      {/* === UI OVERLAYS === */}
      <WeatherOverlay center={mapCenter} visible={config.weather} />

      <MapControls
        config={config}
        onToggle={(key) => {
          setConfig(prev => ({ ...prev, [key]: !prev[key] }));
          if (key === 'heatmap' && !config.heatmap) {
            toast.success('Revenue Heatmap Activated');
          }
        }}
      />

    </div>
  );
}
